import { api } from './api';
import type {
  ResourceNodeEquipmentCatalog,
  ResourceNodeEquipmentCapabilityPayload,
  ResourceNodeEquipmentCategoryPayload,
  ResourceNodeEquipmentModelPayload,
} from '../types/resourceNodeEquipmentCatalog';

const CATALOG_API_PATH = '/resource-node-equipment-catalog';

const DEFAULT_FACILITY = (process.env.REACT_APP_PROD_RESOURCE_FACILITY_CODES || 'F1')
  .split(',')[0]?.trim().toUpperCase() || 'F1';

const toCategoryPayload = (payload: ResourceNodeEquipmentCategoryPayload) => ({
  category_name: payload.categoryName,
  description: payload.description,
  sort_order: payload.sortOrder,
  is_active: payload.isActive,
});

const toCapabilityPayload = (payload: ResourceNodeEquipmentCapabilityPayload) => ({
  capability_name: payload.capabilityName,
  capacity_dimension: payload.capacityDimension,
  description: payload.description,
  sort_order: payload.sortOrder,
  is_active: payload.isActive,
});

const toModelPayload = (payload: ResourceNodeEquipmentModelPayload) => ({
  category_id: payload.categoryId,
  model_name: payload.modelName,
  description: payload.description,
  sort_order: payload.sortOrder,
  is_active: payload.isActive,
  facility_code: payload.facilityCode,
  capabilities: payload.capabilities.map((item) => ({
    capability_id: item.capabilityId,
    min_capacity: item.minCapacity,
    max_capacity: item.maxCapacity,
  })),
  state_profile_id: payload.stateProfileId,
});

export const resourceNodeEquipmentCatalogApi = {
  async getCatalog(facilityCode = DEFAULT_FACILITY): Promise<ResourceNodeEquipmentCatalog> {
    const response = await api.get(CATALOG_API_PATH, { params: { facilityCode } });
    return response.data;
  },

  async createCategory(payload: ResourceNodeEquipmentCategoryPayload): Promise<void> {
    await api.post(`${CATALOG_API_PATH}/categories`, toCategoryPayload(payload));
  },

  async createCapability(payload: ResourceNodeEquipmentCapabilityPayload): Promise<void> {
    await api.post(`${CATALOG_API_PATH}/capabilities`, toCapabilityPayload(payload));
  },

  async updateCapability(
    capabilityId: number,
    payload: ResourceNodeEquipmentCapabilityPayload,
  ): Promise<void> {
    await api.patch(`${CATALOG_API_PATH}/capabilities/${capabilityId}`, toCapabilityPayload(payload));
  },

  async updateCategory(categoryId: number, payload: ResourceNodeEquipmentCategoryPayload): Promise<void> {
    await api.patch(`${CATALOG_API_PATH}/categories/${categoryId}`, toCategoryPayload(payload));
  },

  async createModel(payload: ResourceNodeEquipmentModelPayload): Promise<void> {
    await api.post(`${CATALOG_API_PATH}/models`, toModelPayload(payload));
  },

  async updateModel(modelId: number, payload: ResourceNodeEquipmentModelPayload): Promise<void> {
    await api.patch(`${CATALOG_API_PATH}/models/${modelId}`, toModelPayload(payload));
  },
};
